import moment from 'moment';
import { useState, createContext, useCallback, useContext, useRef, useEffect } from 'react';

import { PORTFOLIO_TABS_NAME } from '../../constants/reports/reports-data';
import {
  RISK_METRIC_LIST,
  excludeDisabledRiskMetrics,
} from '../../constants/reports/reports-filters';
import usePersistentState from '../use-persistent-state';
import { IAccountOption } from '../venue-provider';

import { DemoBlurContext } from '../demo-blur';

import { updateOrCreateAccountData } from './account-data-helpers';
import { updateOrCreateAggregatedData } from './aggregated-data-helpers';
import {
  DATA_DISPLAY_KEYS,
  RISK_SETTINGS_TIMES,
  RISK_SETTINGS_VOL_SHOCKS,
} from './portfolio-management.constants';
import {
  DataParent,
  ExchangeType,
  IAddDataToAccount,
  IAddGeneralAggregatedData,
  IAddSpotDataToAccount,
  IAggregatedData,
  IAggregatedDataAccount,
  IGeneralSettings,
  IMapDataAccounts,
  IPortfolioManagementContext,
  IPortfolioManagementParamsProvider,
  IRiskSettings,
  SelectedRiskSettingsProps,
} from './portfolio-management.types';
import { useIndexedDBState } from '../use-indexeddb-state';

export const PortfolioManagementContext = createContext<IPortfolioManagementContext>(
  {} as IPortfolioManagementContext,
);

export const PortfolioManagementProvider = ({ children }: IPortfolioManagementParamsProvider) => {
  const PATH = ['portfolio-management'];
  const [isPortfolioLoading, setIsPortfolioLoading] = useState<boolean>(false);
  const [accountTabs, setAccountTabs] = useState<{ [key: string]: string }>({});
  const [selectedDataDisplayOption, setSelectedDataDisplayOption] = usePersistentState<string>(
    PATH,
    'selectedDataDisplayOption',
    DATA_DISPLAY_KEYS.perAccount,
  );
  const [accountCalculationStatus, setAccountCalculationStatus] = useState<{
    [key: string]: boolean;
  }>({});
  const [aggregatedCalculationStatus, setAggregatedCalculationStatus] = useState<boolean>(false);
  const [selectedAccountOption, setSelectedAccountOption] = usePersistentState<IAccountOption>(
    PATH,
    'selectedAccountOption',
    {} as IAccountOption,
  );
  const [selectedAccountsAggregated, setSelectedAccountsAggregated] = usePersistentState<
    IAccountOption[]
  >(PATH, 'selectedAccountsAggregated', []);
  const [isPortfolioSidebarSettingsOpen, setIsPortfolioSidebarSettingsOpen] = useState(false);

  const [generalSettingsSelected, setGeneralSettingsSelected] =
    usePersistentState<IGeneralSettings>(['GENERAL_SETTINGS'], 'generalSettings', {
      accountListing: 'byExchange',
    });

  const [riskSettingsSelected, setRiskSettingsSelected] = usePersistentState<IRiskSettings>(
    ['RISK_FILTERS'],
    'riskSettings',
    {
      volShocks: [0],
      times: ['0D'],
      riskMetrics: excludeDisabledRiskMetrics(RISK_METRIC_LIST.map((risk) => risk.value)),
      isSpotOverride: false,
      isFreezeSpotOverride: false,
      freezeSpotValue: undefined,
      isOtcTraderVols: false,
      showRiskCharts: false,
    },
  );

  const [selectedRiskSettingsVolShocks, setSelectedRiskSettingsVolShocks] =
    useState<SelectedRiskSettingsProps[]>(RISK_SETTINGS_VOL_SHOCKS);
  const [selectedRiskSettingsTimes, setSelectedRiskSettingsTimes] =
    useState<SelectedRiskSettingsProps[]>(RISK_SETTINGS_TIMES);

  const accountsAggregatedRef = useRef<IAccountOption[]>([]);

  const [mapDataAccounts, setMapDataAccounts] = useState<IMapDataAccounts>({
    binance: [],
    coincall: [],
    bullish: [],
    bybit: [],
    deribit: [],
    okx: [],
    otc: [],
  });

  const [aggregatedData, setAggregatedData] = useIndexedDBState<IAggregatedData>(
    'risk-aggregatedData',
    {
      dataAccounts: [],
      isCalculated: false,
      accounts: [],
    },
  );

  const [spotOverrideValue, setSpotOverrideValue] = useState('');
  // Shared, persisted demo-mode switch (one toggle across PP / PM / Account
  // Management). Re-exposed on this context so PM consumers are unchanged.
  const { isNamesBlurred, setIsNamesBlurred } = useContext(DemoBlurContext);
  const freezeSpotMapRef = useRef<Record<string, number>>({});

  const addSpotDataToAccount = useCallback(
    ({
      account,
      dataParent,
      data,
      error,
      riskToken,
      timestamp,
      riskMetrics,
      isSpotOverride,
      isFreezeSpotOverride,
      freezeSpotValue,
      isOtcTraderVols,
    }: IAddSpotDataToAccount) => {
      setMapDataAccounts((prev) => {
        const updatedAccounts: IMapDataAccounts = { ...prev };
        const exchangeKey = account.exchange as ExchangeType;

        const updateAccountData = (dataAccount: any) => {
          if (riskToken) {
            const key = riskToken.toLowerCase();
            dataAccount.data[key] = data;
            dataAccount.error[key] = error;
          } else {
            dataAccount.data = data;
            dataAccount.error = error;
          }
          dataAccount.lastUpdate = timestamp ?? moment().utc().toISOString();
          dataAccount.riskMetrics = riskMetrics;
          dataAccount.isSpotOverride = isSpotOverride;
          dataAccount.isFreezeSpotOverride = isFreezeSpotOverride;
          dataAccount.freezeSpotValue = freezeSpotValue;
          dataAccount.isOtcTraderVols = isOtcTraderVols;
        };

        const createNewDataAccount = () => {
          const newDataAccount: any = {
            dataParent,
            data: {},
            error: {},
            lastUpdate: timestamp ?? moment().utc().toISOString(),
            riskMetrics,
            isSpotOverride,
            isFreezeSpotOverride,
            freezeSpotValue,
            isOtcTraderVols,
          };

          if (riskToken) {
            const key = riskToken.toLowerCase();
            newDataAccount.data[key] = data;
            newDataAccount.error[key] = error;
          } else {
            newDataAccount.data = data;
            newDataAccount.error = error;
          }
          return newDataAccount;
        };

        return updateOrCreateAccountData(
          updatedAccounts,
          exchangeKey,
          account,
          dataParent,
          updateAccountData,
          createNewDataAccount,
        );
      });
    },
    [],
  );

  const addDataToAccount = useCallback(
    ({ account, dataParent, data, error, pnlRiskToken, bookErrors }: IAddDataToAccount) => {
      setMapDataAccounts((prev) => {
        const updatedAccounts: IMapDataAccounts = { ...prev };
        const exchangeKey = account.exchange as ExchangeType;

        const updateExistingDataAccount = (dataAccount: any) => {
          if (dataParent === 'PNL' && pnlRiskToken) {
            dataAccount.data[pnlRiskToken] = data;
            dataAccount.error[pnlRiskToken] = error;
            dataAccount.bookErrors = bookErrors;
          } else {
            dataAccount.data = data;
            dataAccount.error = error;
          }
          dataAccount.lastUpdate = moment().utc().toISOString();
        };

        const createNewDataAccount = () => {
          const newDataAccount: any = {
            dataParent,
            data: {},
            error: {},
            bookErrors: [],
            lastUpdate: moment().utc().toISOString(),
          };

          if (dataParent === 'PNL' && pnlRiskToken) {
            newDataAccount.data[pnlRiskToken] = data;
            newDataAccount.error[pnlRiskToken] = error;
            newDataAccount.bookErrors = bookErrors;
          } else {
            newDataAccount.data = data;
            newDataAccount.error = error;
          }
          return newDataAccount;
        };

        return updateOrCreateAccountData(
          updatedAccounts,
          exchangeKey,
          account,
          dataParent,
          updateExistingDataAccount,
          createNewDataAccount,
        );
      });
    },
    [mapDataAccounts],
  );

  const addAggregatedData = useCallback(
    ({
      dataParent,
      data,
      error,
      exchange,
      riskToken,
      riskMetrics,
      isSpotOverride,
      isFreezeSpotOverride,
      freezeSpotValue,
      isOtcTraderVols,
    }: any) => {
      setAggregatedData((prev) => {
        const updatedDataAccounts = [...prev.dataAccounts];
        const key = riskToken.toLowerCase();

        const updateData = (account: IAggregatedDataAccount) => {
          account.data[key] = account.data[key] || {};
          account.error[key] = account.error[key] || {};
          account.data[key][exchange] = data;
          account.error[key][exchange] = error;
          account.lastUpdate = moment().utc().toISOString();
          account.riskMetrics = riskMetrics;
          account.isSpotOverride = isSpotOverride;
          account.isFreezeSpotOverride = isFreezeSpotOverride;
          account.freezeSpotValue = freezeSpotValue;
          account.isOtcTraderVols = isOtcTraderVols;
        };

        const createData = (): IAggregatedDataAccount => ({
          dataParent,
          lastUpdate: moment().utc().toISOString(),
          riskMetrics,
          isSpotOverride,
          isFreezeSpotOverride,
          freezeSpotValue,
          isOtcTraderVols,
          data: { [key]: { [exchange]: data } },
          error: { [key]: { [exchange]: error } },
        });

        const newDataAccounts = updateOrCreateAggregatedData(
          updatedDataAccounts,
          dataParent,
          updateData,
          createData,
        );

        return {
          ...prev,
          dataAccounts: newDataAccounts,
          accounts: accountsAggregatedRef.current,
          isCalculated: true,
        };
      });
    },
    [],
  );

  const addGeneralAggregatedData = useCallback(
    ({
      dataParent,
      data,
      error,
      exchange,
      pnlRiskToken,
      bookErrors,
    }: IAddGeneralAggregatedData) => {
      setAggregatedData((prev) => {
        const updatedDataAccounts = [...prev.dataAccounts];

        const updateData = (account: IAggregatedDataAccount) => {
          if (dataParent === PORTFOLIO_TABS_NAME.strikeReport) {
            account.data = data;
            account.error = error;
          } else if (dataParent === PORTFOLIO_TABS_NAME.pnl && pnlRiskToken) {
            account.data[pnlRiskToken] = data;
            account.error[pnlRiskToken] = error;
            account.bookErrors = bookErrors || [];
          } else {
            account.data[exchange] = account.data[exchange] || {};
            account.error[exchange] = account.error[exchange] || {};
            account.data[exchange] = data;
            account.error[exchange] = error;
          }
          account.lastUpdate = moment().utc().toISOString();
        };

        const createData = (): IAggregatedDataAccount => {
          const base = {
            dataParent,
            lastUpdate: moment().utc().toISOString(),
            bookErrors: [] as any[],
          };

          if (dataParent === PORTFOLIO_TABS_NAME.strikeReport) {
            return {
              ...base,
              data,
              error,
            };
          } else if (dataParent === PORTFOLIO_TABS_NAME.pnl && pnlRiskToken) {
            return {
              ...base,
              data: { [pnlRiskToken]: data },
              error: { [pnlRiskToken]: error },
              bookErrors: bookErrors || [],
            };
          } else {
            return {
              ...base,
              data: { [exchange]: data },
              error: { [exchange]: error },
              bookErrors: [],
            };
          }
        };

        const newDataAccounts = updateOrCreateAggregatedData(
          updatedDataAccounts,
          dataParent,
          updateData,
          createData,
        );

        return {
          ...prev,
          dataAccounts: newDataAccounts,
          accounts: accountsAggregatedRef.current,
          isCalculated: true,
        };
      });
    },
    [],
  );

  const getDataSpotFromAccount = useCallback(
    (accountOption: IAccountOption, dataParent: DataParent, riskToken: string) => {
      const key = riskToken.toLowerCase();
      const exchangeKey = accountOption.exchange as ExchangeType;
      const account = mapDataAccounts[exchangeKey]?.find((acc) => acc.name === accountOption.name);
      if (!account) return null;
      const dataAccount = account.dataAccount.find((item) => item.dataParent === dataParent);
      if (!dataAccount) return null;
      const spotData = dataAccount.data[key];
      if (!spotData) return null;
      return {
        data: spotData,
        exchange: account.exchange,
        lastUpdate: dataAccount.lastUpdate,
        error: dataAccount.error[key],
        riskMetrics: dataAccount.riskMetrics,
        isSpotOverride: dataAccount.isSpotOverride,
        isFreezeSpotOverride: dataAccount.isFreezeSpotOverride,
        freezeSpotValue: dataAccount.freezeSpotValue,
        isOtcTraderVols: dataAccount.isOtcTraderVols,
      };
    },
    [mapDataAccounts],
  );

  const getAggregatedDataSpot = useCallback(
    (dataParent: DataParent, riskToken: string) => {
      const account = aggregatedData.dataAccounts.find(
        (account) => account.dataParent === dataParent,
      );
      if (!account) return null;
      const key = riskToken.toLowerCase();
      const spotData = account.data[key];
      if (!spotData || Object.keys(spotData).length === 0) return null;
      return {
        data: spotData,
        error: account.error[key],
        lastUpdate: account.lastUpdate,
        accounts: aggregatedData.accounts,
        riskMetrics: account.riskMetrics,
        isSpotOverride: account.isSpotOverride,
        isFreezeSpotOverride: account.isFreezeSpotOverride,
        freezeSpotValue: account.freezeSpotValue,
        isOtcTraderVols: account.isOtcTraderVols,
      };
    },
    [aggregatedData],
  );

  const getDataFromAccount = useCallback(
    (dataParent: DataParent, accountOption: IAccountOption, pnlRiskToken?: string) => {
      const exchangeKey = accountOption.exchange as ExchangeType;
      const account = mapDataAccounts[exchangeKey]?.find((acc) => acc.name === accountOption.name);
      if (!account) return null;
      const dataAccount = account.dataAccount.find((item) => item.dataParent === dataParent);
      if (!dataAccount) return null;
      const { lastUpdate, data, error, bookErrors } = dataAccount;
      if (dataParent === 'PNL' && pnlRiskToken) {
        const pnlData = data[pnlRiskToken];
        const pnlError = error[pnlRiskToken];

        return pnlData
          ? { data: pnlData, exchange: account.exchange, lastUpdate, error: pnlError, bookErrors }
          : null;
      }
      return data ? { data, exchange: account.exchange, lastUpdate, error } : null;
    },
    [mapDataAccounts],
  );

  const getAggregatedData = useCallback(
    (dataParent: DataParent) => {
      const account = aggregatedData.dataAccounts.find((acc) => acc.dataParent === dataParent);
      if (!account) return null;
      const { data, lastUpdate, error } = account;
      return {
        data,
        lastUpdate,
        accounts: aggregatedData.accounts,
        bookErrors: account.bookErrors,
        error,
      };
    },
    [aggregatedData],
  );

  const getDataDetailsAccount = useCallback(
    (accountOption: IAccountOption) => {
      const exchangeKey = accountOption.exchange as ExchangeType;
      return mapDataAccounts[exchangeKey]?.find((acc) => acc.name === accountOption.name) || null;
    },
    [mapDataAccounts],
  );

  const getDataDetailsAggregatedAccounts = useCallback(
    (dataParent: DataParent) =>
      aggregatedData.dataAccounts.find((acc) => acc.dataParent === dataParent)
        ? { isCalculated: aggregatedData.isCalculated }
        : null,
    [aggregatedData],
  );

  const clearAccountData = useCallback(
    (accountOption: IAccountOption, dataParentToClear?: string) => {
      setMapDataAccounts((prev) => {
        const exchangeKey = accountOption.exchange as ExchangeType;

        const updated = prev[exchangeKey].map((acc) => {
          if (acc.name !== accountOption.name) return acc;

          if (!dataParentToClear) {
            return { ...acc, dataAccount: [] };
          }

          const dataAccount = acc.dataAccount.map((entry) =>
            entry.dataParent === dataParentToClear
              ? {
                  ...entry,
                  data: {},
                  error: {},
                }
              : entry,
          );

          return { ...acc, dataAccount };
        });

        return { ...prev, [exchangeKey]: updated };
      });
    },
    [setMapDataAccounts],
  );

  const clearAggregatedData = useCallback(
    (dataParentToClear?: string) => {
      setAggregatedData((prev: any) => {
        if (!dataParentToClear) {
          return {
            ...prev,
            dataAccounts: [],
          };
        }

        const dataAccounts = prev.dataAccounts.map((entry: any) =>
          entry.dataParent === dataParentToClear
            ? {
                ...entry,
                data: {},
                error: {},
              }
            : entry,
        );

        return {
          ...prev,
          dataAccounts,
        };
      });
    },
    [setAggregatedData],
  );

  const isDataAccountEmpty = useCallback(
    (accountOption: IAccountOption): boolean => {
      const exchangeKey = accountOption.exchange as ExchangeType;
      const account = mapDataAccounts[exchangeKey]?.find((acc) => acc.name === accountOption.name);
      return !account || account.dataAccount.length === 0;
    },
    [mapDataAccounts],
  );

  const handleCalculateAccountStatus = (accountName: string) => {
    setAccountCalculationStatus((prevStatus) => ({
      ...prevStatus,
      [accountName]: true,
    }));
  };

  useEffect(() => {
    accountsAggregatedRef.current = selectedAccountsAggregated;
  }, [selectedAccountsAggregated]);

  return (
    <PortfolioManagementContext.Provider
      value={{
        isPortfolioLoading,
        setIsPortfolioLoading,
        selectedAccountOption,
        setSelectedAccountOption,
        selectedAccountsAggregated,
        setSelectedAccountsAggregated,
        selectedDataDisplayOption,
        setSelectedDataDisplayOption,
        generalSettingsSelected,
        setGeneralSettingsSelected,
        riskSettingsSelected,
        setRiskSettingsSelected,
        accountTabs,
        accountCalculationStatus,
        handleCalculateAccountStatus,
        aggregatedCalculationStatus,
        setAggregatedCalculationStatus,
        setAccountTabs,
        mapDataAccounts,
        aggregatedData,
        addDataToAccount,
        addSpotDataToAccount,
        addAggregatedData,
        addGeneralAggregatedData,
        getDataFromAccount,
        getDataSpotFromAccount,
        getAggregatedDataSpot,
        getAggregatedData,
        getDataDetailsAccount,
        getDataDetailsAggregatedAccounts,
        clearAccountData,
        clearAggregatedData,
        isDataAccountEmpty,
        selectedRiskSettingsVolShocks,
        setSelectedRiskSettingsVolShocks,
        selectedRiskSettingsTimes,
        setSelectedRiskSettingsTimes,
        isPortfolioSidebarSettingsOpen,
        setIsPortfolioSidebarSettingsOpen,
        spotOverrideValue,
        setSpotOverrideValue,
        freezeSpotMapRef,
        isNamesBlurred,
        setIsNamesBlurred,
      }}
    >
      {children}
    </PortfolioManagementContext.Provider>
  );
};
